// src/pages/LandingPage.js
import React from "react";
import { useNavigate } from "react-router-dom";

const LandingPage = () => {
    const navigate = useNavigate();

    return (
        <div className="relative min-h-screen bg-slate-950 text-slate-100 flex flex-col overflow-x-hidden">
            {/* Top gradient background */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-violet-900/40 via-slate-950 to-slate-950" />

            {/* Glow blobs */}
            <div className="pointer-events-none absolute -top-40 -right-32 h-80 w-80 rounded-full bg-violet-600/30 blur-3xl" />
            <div className="pointer-events-none absolute top-40 -left-32 h-72 w-72 rounded-full bg-cyan-500/25 blur-3xl" />

            {/* Navbar */}
            <header className="w-full px-6 md:px-12 py-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-violet-500 to-cyan-400 flex items-center justify-center text-lg font-bold">
                        S
                    </div>
                    <span className="font-semibold  tracking-tight text-lg">
                        SlickAPI
                    </span>
                </div>
                <nav className="flex items-center gap-4 text-xs sm:text-sm text-slate-300">
                    <button
                        onClick={() => {
                            const el = document.getElementById("features");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="hover:text-white"
                    >
                        Features
                    </button>
                    <button
                        onClick={() => {
                            const el = document.getElementById("usecases");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="hover:text-white"
                    >
                        Use Cases
                    </button>
                    <button
                        onClick={() => navigate("/pricing")}
                        className="hover:text-white"
                    >
                        Pricing
                    </button>
                    <button
                        onClick={() => {
                            const el = document.getElementById("api");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="hover:text-white"
                    >
                        API
                    </button>
                </nav>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/login")}
                        className="hidden sm:inline-flex text-sm px-4 py-1.5 rounded-full border border-slate-600 text-slate-200 hover:border-slate-300"
                    >
                        Log in
                    </button>
                    <button
                        onClick={() => navigate("/signup")}
                        className="text-sm px-4 py-1.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 text-slate-950 font-semibold hover:brightness-110"
                    >
                        Get early access
                    </button>
                </div>
            </header>

            {/* Hero */}
            <main className="flex-1 px-6 md:px-12 pb-16">
                <section className="max-w-5xl mx-auto pt-10 md:pt-16 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="inline-flex items-center text-xs font-medium uppercase tracking-[0.25em] text-violet-300/80 mb-4">
                            AI infra for builders
                        </p>
                        <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                            A single API for
                            <span className="block bg-gradient-to-r from-violet-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                                Llama&nbsp;3.2, GPT-OSS, DeepSeek-R1
                            </span>
                        </h1>
                        <p className="text-sm md:text-base text-slate-300 max-w-xl mb-6">
                            SlickAPI is your inference layer for modern open-source LLMs.
                            Secure, rate-limited endpoints, simple billing, and production-ready
                            streaming responses — so you can ship AI features in hours, not weeks.
                        </p>
                        <div className="flex flex-wrap gap-3 mb-6">
                            <button
                                onClick={() => navigate("/signup")}
                                className="px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 text-slate-950 font-semibold text-sm hover:brightness-110"
                            >
                                Start free trial
                            </button>
                            <button
                                onClick={() => {
                                    const el = document.getElementById("api");
                                    if (el) el.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="px-5 py-2.5 rounded-full border border-slate-600 text-slate-200 text-sm hover:border-slate-300"
                            >
                                View API examples
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                            <span className="inline-flex items-center gap-1">
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                Live streaming responses
                            </span>
                            <span>Rate-limited per subscription</span>
                            <span>API keys </span>
                        </div>
                    </div>

                    {/* Futuristic "console" / preview */}
                    <div className="relative">
                        <div className="absolute -inset-1 bg-gradient-to-tr from-violet-500/40 via-cyan-400/40 to-emerald-300/40 rounded-3xl blur-xl opacity-40" />
                        <div className="relative bg-slate-900/70 border border-slate-700/80 rounded-3xl p-5 backdrop-blur shadow-xl">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex gap-1.5">
                                    <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                                </div>
                                <span className="text-[10px] text-slate-400">
                                    slickapi.elephantrohck.com
                                </span>
                            </div>

                            <div className="text-[11px] font-mono bg-black/60 rounded-2xl p-4 text-emerald-100 overflow-x-auto">
                                <span className="text-slate-500"># cURL example</span>
                                {"\n"}curl -N -X POST{" "}
                                <span className="text-cyan-300">
                                    "https://api.elephantrohck.com/llama-query"
                                </span>
                                {" \\\n  -H \"Content-Type: application/json\" \\\n  -H \"X-API-Key: "}
                                <span className="text-amber-300">YOUR_API_KEY</span>
                                {"\" \\\n  -d '{\n    \"prompt\": \"Hello from SlickAPI\" \n  }'"}
                                {"\n\n"}
                                <span className="text-slate-500"># Response (streamed)</span>
                                {"\n"}&gt; SlickAPI lets you ship production LLM features in minutes…
                            </div>

                            <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-slate-300">
                                <div className="rounded-xl bg-slate-900/80 border border-violet-500/30 px-3 py-2">
                                    <div className="text-[9px] uppercase tracking-[0.18em] text-violet-300 mb-1">
                                        Models
                                    </div>
                                    <div>Llama 3.2</div>
                                    <div>GPT-OSS 20B</div>
                                    <div>DeepSeek-R1</div>
                                </div>
                                <div className="rounded-xl bg-slate-900/80 border border-cyan-500/30 px-3 py-2">
                                    <div className="text-[9px] uppercase tracking-[0.18em] text-cyan-300 mb-1">
                                        Features
                                    </div>
                                    <div>Streaming</div>
                                    <div>API Keys</div>
                                    <div>Daily limits</div>
                                </div>
                                <div className="rounded-xl bg-slate-900/80 border border-emerald-500/30 px-3 py-2">
                                    <div className="text-[9px] uppercase tracking-[0.18em] text-emerald-300 mb-1">
                                        Built For
                                    </div>
                                    <div>SaaS apps</div>
                                    <div>Agents</div>
                                    <div>Side-projects</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section id="features" className="max-w-5xl mx-auto mt-16">
                    <h2 className="text-xl md:text-2xl font-semibold mb-4">
                        Why SlickAPI?
                    </h2>
                    <p className="text-sm md:text-base text-slate-300 mb-6 max-w-3xl">
                        Instead of fighting infra, quotas and fragmented APIs, SlickAPI gives
                        you a unified, opinionated layer on top of your favorite open-source
                        models. Think “Stripe, but for LLM inference”.
                    </p>

                    <div className="grid md:grid-cols-3 gap-5 text-sm">
                        <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-4">
                            <div className="text-violet-300 font-semibold mb-1">
                                Multi-model, one API
                            </div>
                            <p className="text-slate-300 text-xs">
                                Call <code>/llama-query</code>, <code>/gpt-oss-query</code> or{" "}
                                <code>/deepseek-query</code> with the same request shape.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-4">
                            <div className="text-cyan-300 font-semibold mb-1">
                                Streaming by default
                            </div>
                            <p className="text-slate-300 text-xs">
                                All endpoints stream text chunks so your UI feels instant.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-4">
                            <div className="text-emerald-300 font-semibold mb-1">
                                Production-ready limits
                            </div>
                            <p className="text-slate-300 text-xs">
                                Per-plan daily request limits, tracked server-side in MySQL, tied
                                to subscriptions.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Use cases */}
                <section id="usecases" className="max-w-5xl mx-auto mt-16">
                    <h2 className="text-xl md:text-2xl font-semibold mb-4">
                        Built for the next billion AI products
                    </h2>
                    <div className="grid md:grid-cols-3 gap-5 text-sm">
                        <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-4">
                            <div className="font-semibold mb-1">Developer tooling</div>
                            <p className="text-slate-300 text-xs">
                                Code assistants, CLI tools, browser extensions and internal dev
                                dashboards.
                            </p>
                        </div>
                        <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-4">
                            <div className="font-semibold mb-1">SaaS & customer apps</div>
                            <p className="text-slate-300 text-xs">
                                Chatbots, support copilots, document Q&A, AI workflows for your
                                paying customers.
                            </p>
                        </div>
                        <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-4">
                            <div className="font-semibold mb-1">Agents & automation</div>
                            <p className="text-slate-300 text-xs">
                                Multi-step agents that call your own APIs while SlickAPI handles
                                the LLM layer.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Pricing (high-level copy only) */}
                <section id="pricing" className="max-w-5xl mx-auto mt-16">
                    <h2 className="text-xl md:text-2xl font-semibold mb-4">
                        Simple, subscription-based access
                    </h2>
                    <p className="text-sm md:text-base text-slate-300 mb-6 max-w-3xl">
                        Each plan unlocks daily request quotas for a specific model family — Llama 3.2, GPT-OSS or DeepSeek-R1 — all visible inside your dashboard.
                    </p>
                    <div className="grid md:grid-cols-3 gap-5 text-sm">
                        <div className="bg-slate-900/70 border border-violet-500/40 rounded-2xl p-4">
                            <div className="text-violet-300 font-semibold mb-2">
                                Llama 3.2 Starter
                            </div>
                            <p className="text-slate-300 text-xs mb-3">
                                Perfect for side projects, prototypes and demos.
                            </p>
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="text-xs px-3 py-1.5 rounded-full bg-violet-500/90 text-slate-950 font-semibold hover:brightness-110"
                            >
                                View plan in Dashboard
                            </button>
                        </div>
                        <div className="bg-slate-900/70 border border-cyan-500/40 rounded-2xl p-4">
                            <div className="text-cyan-300 font-semibold mb-2">
                                GPT-OSS Builder
                            </div>
                            <p className="text-slate-300 text-xs mb-3">
                                For apps that need a larger, more capable model.
                            </p>
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="text-xs px-3 py-1.5 rounded-full bg-cyan-400/90 text-slate-950 font-semibold hover:brightness-110"
                            >
                                View plan in Dashboard
                            </button>
                        </div>
                        <div className="bg-slate-900/70 border border-emerald-500/40 rounded-2xl p-4">
                            <div className="text-emerald-300 font-semibold mb-2">
                                DeepSeek R1 Experimental
                            </div>
                            <p className="text-slate-300 text-xs mb-3">
                                For reasoning-heavy experiments and agent research.
                            </p>
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="text-xs px-3 py-1.5 rounded-full bg-emerald-400/90 text-slate-950 font-semibold hover:brightness-110"
                            >
                                View plan in Dashboard
                            </button>
                        </div>
                    </div>
                </section>

                {/* API examples preview section */}
                <section id="api" className="max-w-5xl mx-auto mt-16 mb-10">
                    <h2 className="text-xl md:text-2xl font-semibold mb-4">
                        Quick API examples
                    </h2>
                    <p className="text-sm md:text-base text-slate-300 mb-4">
                        Use your API key from the Dashboard and call these endpoints from any
                        environment.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4 text-xs font-mono">
                        <div className="bg-black/70 border border-slate-700 rounded-2xl p-3 text-emerald-100 overflow-x-auto">
                            <div className="text-[11px] text-violet-300 mb-1">cURL</div>
                            <pre>{`curl -N -X POST "https://api.elephantrohck.com/llama-query" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "prompt": "Hello from SlickAPI"
  }'`}</pre>
                        </div>
                        <div className="bg-black/70 border border-slate-700 rounded-2xl p-3 text-emerald-100 overflow-x-auto">
                            <div className="text-[11px] text-cyan-300 mb-1">Python</div>
                            <pre>{`import requests

url = "https://api.elephantrohck.com/llama-query"
headers = {
  "X-API-Key": "YOUR_API_KEY",
  "Content-Type": "application/json",
}
data = {"prompt": "Hello from SlickAPI"}

resp = requests.post(url, json=data, headers=headers, stream=True)
for chunk in resp.iter_content(chunk_size=None):
    if chunk:
        print(chunk.decode(), end="")`}</pre>
                        </div>
                        <div className="bg-black/70 border border-slate-700 rounded-2xl p-3 text-emerald-100 overflow-x-auto">
                            <div className="text-[11px] text-emerald-300 mb-1">Node.js</div>
                            <pre>{`import fetch from "node-fetch";

const resp = await fetch(
  "https://api.elephantrohck.com/llama-query",
  {
    method: "POST",
    headers: {
      "X-API-Key": "YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ prompt: "Hello from SlickAPI" }),
  }
);

for await (const chunk of resp.body) {
  process.stdout.write(chunk.toString());
}`}</pre>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-800 py-4 px-6 md:px-12 text-xs text-slate-500 flex justify-between items-center">
                <span>© {new Date().getFullYear()} SlickAPI · ElephantRohck</span>
                <span>Made for builders of the AI future </span>
            </footer>
        </div>
    );
};

export default LandingPage;
